// Follow-ups list, report and detail queries, and the view/filter state they read.

import { atom } from 'jotai';
import { atomWithInfiniteQuery, atomWithQuery } from 'jotai-tanstack-query';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import { sentinelTicketStatusAtom } from '@/hooks/use-sentinel-routing';
import { followupsService } from '@/services/followups.service';
import { toFollowupSearchString } from './followup-query';
import { EMPTY_FOLLOWUP_FILTERS, type FollowupFilters, type FollowupPage, type FollowupView } from './types';

const PAGE_SIZE = 50;
// Polled while the tab is visible (react-query pauses interval refetches in a background tab).
const REFRESH_MS = 30_000;

export const followupViewAtom = atom<FollowupView>('due');
export const followupFiltersAtom = atom<FollowupFilters>(EMPTY_FOLLOWUP_FILTERS);

/** Workflows seen in the list, by id, so the switch's Workflow submenu can offer them. */
export const followupWorkflowsAtom = atom<Map<string, string>>(new Map());

export const followupListQueryAtom = atomWithInfiniteQuery((get) => {
  const view = get(followupViewAtom);
  const filters = get(followupFiltersAtom);
  const ticketStatus = get(sentinelTicketStatusAtom);
  return {
    // The brand is in the key: a brand switch does not clear the cache.
    queryKey: ['sentinel', 'followups', 'list', buScopeKey(get), toFollowupSearchString({ view, filters, ticketStatus })],
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      followupsService.list({ view, filters, ticketStatus, limit: PAGE_SIZE, cursor: pageParam }, buScopeParams(get)),
    initialPageParam: null as string | null,
    getNextPageParam: (last: FollowupPage) => last.nextCursor ?? undefined,
    refetchInterval: REFRESH_MS,
    // A 400 names a bad parameter; retrying cannot fix it, and the message must show.
    retry: false,
  };
});

export const followupReportQueryAtom = atomWithQuery((get) => ({
  queryKey: ['sentinel', 'followups', 'report', buScopeKey(get)],
  queryFn: () => followupsService.report(buScopeParams(get)),
  refetchInterval: REFRESH_MS,
  retry: false,
}));

/** Tab counts only (they ignore the view and filters), for the workspace switch. */
export const followupCountsQueryAtom = atomWithQuery((get) => ({
  queryKey: ['sentinel', 'followups', 'counts', buScopeKey(get)],
  queryFn: async () =>
    (await followupsService.list({ view: 'drafts', filters: EMPTY_FOLLOWUP_FILTERS, limit: 1 }, buScopeParams(get))).counts,
  refetchInterval: REFRESH_MS,
  retry: false,
}));

export const openFollowupIdAtom = atom<string | null>(null);

export const followupDetailQueryAtom = atomWithQuery((get) => {
  const id = get(openFollowupIdAtom);
  return {
    queryKey: ['sentinel', 'followups', 'detail', buScopeKey(get), id],
    queryFn: () => followupsService.get(id as string, buScopeParams(get)),
    enabled: id !== null,
    // Its thread shows in the Follow-ups view, so new messages must arrive without a reload.
    refetchInterval: REFRESH_MS,
    retry: false,
  };
});
